import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useWalletConnection } from "../../hooks/useWalletConnection";
import { WalletSvg } from "../svg/svgFilesGlobal";
import { MetaMaskIcon } from "../svg/WalletIcons";
import { errorToast, successToast } from "../../utils/toast";

const DashboardWalletButton = ({ className = "", compact = false }) => {
  const {
    isConnected,
    address,
    networkId,
    connectToWallet,
    disconnect,
    isLoading,
    WALLET_PROVIDERS,
  } = useWalletConnection();

  const [showWalletModal, setShowWalletModal] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const modalRef = useRef(null);

  // Ensure portal only renders after hydration
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowWalletModal(false);
      }
    };

    if (showWalletModal) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden"; // Prevent background scroll
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [showWalletModal]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowWalletModal(false);
      }
    };

    if (showWalletModal) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showWalletModal]);

  const handleConnect = async (walletType) => {
    try {
      // connectToWallet RESOLVES with { success, error } — it does not throw —
      // so the result must be inspected. Surface the failure (e.g. "MetaMask is
      // not installed") and keep the modal open so the user can act on it;
      // only close on success.
      const result = await connectToWallet(walletType);
      if (result?.success) {
        successToast("Wallet connected successfully!");
        setShowWalletModal(false);
      } else {
        errorToast(
          result?.error || "Failed to connect wallet. Please try again.",
        );
      }
    } catch (error) {
      console.error("Failed to connect wallet:", error);
      errorToast(error?.message || "Failed to connect wallet. Please try again.");
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect();
      setShowWalletModal(false);
    } catch (error) {
      console.error("Failed to disconnect wallet:", error);
    }
  };

  const formatAddress = (addr) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const handleButtonClick = () => {
    if (isConnected) {
      // If connected, show connection info or disconnect option
      setShowWalletModal(true);
    } else {
      // If not connected, show wallet selection
      setShowWalletModal(true);
    }
  };

  const getNetworkName = (chainId) => {
    switch (chainId) {
      case 1:
        return "Ethereum Mainnet";
      case 5:
        return "Goerli Testnet";
      case 11155111:
        return "Sepolia Testnet";
      case 137:
        return "Polygon Mainnet";
      case 80001:
        return "Polygon Mumbai";
      default:
        return `Chain ID: ${chainId}`;
    }
  };

  return (
    <>
      {/* Wallet Button */}
      {!isConnected ? (
        <button
          onClick={handleButtonClick}
          disabled={isLoading}
          className={`flex items-center justify-center w-12 h-12 rounded-lg border border-jacarta-100 dark:border-jacarta-600 bg-white dark:bg-jacarta-700 text-jacarta-700 dark:text-white hover:bg-accent hover:text-white dark:hover:bg-accent-dark-mode dark:hover:text-white hover:border-accent dark:hover:border-accent-dark-mode transition-all duration-300 ease-in-out hover:shadow-lg hover:scale-105 ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          } ${className}`}
          title="Connect Wallet"
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-accent dark:border-accent-dark-mode border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <WalletSvg className="w-6 h-6" />
          )}
        </button>
      ) : (
        <button
          onClick={handleButtonClick}
          className={`flex items-center ${
            compact ? "justify-center w-10 h-10 px-0" : "space-x-2 px-4 py-2"
          } rounded-lg border border-jacarta-100 dark:border-jacarta-600 bg-white dark:bg-jacarta-700 text-jacarta-700 dark:text-white hover:bg-accent hover:text-white dark:hover:bg-accent-dark-mode dark:hover:text-white hover:border-accent dark:hover:border-accent-dark-mode transition-all duration-300 ease-in-out hover:shadow-lg ${className}`}
          title="Wallet Connected"
        >
          {compact ? (
            <div className="relative">
              <WalletSvg className="w-5 h-5" />
              <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-accent dark:bg-accent-dark-mode rounded-full border-2 border-white dark:border-jacarta-700"></div>
            </div>
          ) : (
            <>
              <div className="w-3 h-3 bg-accent dark:bg-accent-dark-mode rounded-full"></div>
              <span className="text-sm font-medium">
                {formatAddress(address)}
              </span>
            </>
          )}
        </button>
      )}

      {/* Wallet Selection Modal - Using Portal */}
      {isMounted &&
        createPortal(
          <div
            className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-300 ${
              showWalletModal
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            }`}
            style={{
              backgroundColor: showWalletModal
                ? "rgba(0, 0, 0, 0.5)"
                : "rgba(0, 0, 0, 0)",
            }}
          >
            <div
              ref={modalRef}
              className={`bg-white dark:bg-jacarta-700 rounded-lg shadow-xl max-w-md w-full p-6 transition-all duration-300 ${
                showWalletModal
                  ? "opacity-100 scale-100 translate-y-0"
                  : "opacity-0 scale-95 translate-y-4"
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-jacarta-700 dark:text-white">
                  {isConnected ? "Wallet Connected" : "Connect Wallet"}
                </h2>
                <button
                  onClick={() => setShowWalletModal(false)}
                  className="text-jacarta-700 dark:text-white hover:text-accent dark:hover:text-accent-dark-mode transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Connected State */}
              {isConnected ? (
                <div className="space-y-4">
                  <div className="p-4 bg-accent/10 dark:bg-accent-dark-mode/20 border border-accent/20 dark:border-accent-dark-mode/30 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-3 h-3 bg-accent dark:bg-accent-dark-mode rounded-full"></div>
                      <span className="font-medium text-accent dark:text-accent-dark-mode">
                        Wallet Connected
                      </span>
                    </div>
                    <p className="text-sm text-jacarta-700 dark:text-jacarta-300 mb-2">
                      Address:{" "}
                      <code className="bg-jacarta-100 dark:bg-jacarta-600 px-2 py-1 rounded text-xs font-mono text-jacarta-700 dark:text-white">
                        {address?.slice(0, 8)}...{address?.slice(-6)}
                      </code>
                    </p>
                    {networkId && (
                      <p className="text-sm text-jacarta-700 dark:text-jacarta-300">
                        Network: {getNetworkName(networkId)}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleDisconnect}
                    className="w-full px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200 font-medium"
                  >
                    Disconnect Wallet
                  </button>
                </div>
              ) : (
                /* Wallet Selection */
                <div className="space-y-3">
                  <p className="text-sm text-jacarta-700 dark:text-white mb-4">
                    Connect your MetaMask wallet to the platform.
                  </p>

                  {/* MetaMask Option */}
                  <button
                    onClick={() => handleConnect(WALLET_PROVIDERS.METAMASK)}
                    disabled={isLoading}
                    className="w-full flex items-center space-x-4 p-4 border border-jacarta-100 dark:border-jacarta-600 rounded-lg hover:border-accent dark:hover:border-accent-dark-mode hover:bg-accent/5 dark:hover:bg-accent-dark-mode/10 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
                  >
                    <div className="flex-shrink-0">
                      <MetaMaskIcon width={32} height={32} />
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-semibold text-jacarta-700 dark:text-white group-hover:text-accent dark:group-hover:text-accent-dark-mode transition-colors">
                        MetaMask
                      </h3>
                      <p className="text-sm text-jacarta-500 dark:text-jacarta-300">
                        Connect using MetaMask wallet
                      </p>
                    </div>
                    {isLoading && (
                      <div className="w-5 h-5 border-2 border-accent dark:border-accent-dark-mode border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default DashboardWalletButton;
